import {
  DEFAULT_JWT_EXP_SKEW_MS,
  SUBTRACE_GRANT_RESPONSE_KEYS,
} from 'src/services/consts/auth.const'
import { resolveAppLinkOrigin } from 'src/services/helpers/util.helper'

/**
 * Login response refactor: snake_case API payload → camelCase for the frontend.
 *
 * Operates on the object returned by httpService success handling (`response.data`).
 * Never logs bearer tokens or full payloads.
 */

/**
 * Decode a JWT payload without verifying the signature.
 *
 * @param {unknown} token Raw JWT string.
 * @returns {Record<string, unknown> | null}
 */
function decodeJwtPayload(token) {
  if (typeof token !== 'string') return null

  const parts = token.split('.')
  if (parts.length !== 3) return null

  try {
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const base64 = padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '=')
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes))
  } catch {
    return null
  }
}

/**
 * Returns `true` when the token is missing, malformed, or past `exp` (with skew).
 *
 * @param {unknown} token Raw JWT string.
 * @param {{ skewMs?: number }} [options]
 */
export function isBearerTokenExpired(token, { skewMs = DEFAULT_JWT_EXP_SKEW_MS } = {}) {
  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number') return true
  return payload.exp * 1000 <= Date.now() + skewMs
}

/**
 * Map raw login POST body fields to camelCase.
 *
 * @param {unknown} data Raw API payload from `POST /login` (unwrapped `.data`).
 * @returns {{
 *   bearerToken: string | null,
 *   grants: Record<string, { codbId: unknown, companyName: unknown, shareable: unknown, website: unknown }>
 * }}
 * @throws {Error} When the payload is missing or not a plain object (e.g. array or
 *   primitive). Callers (`auth.service` → mutations) treat this like any other thrown
 *   error—`onError` can notify the user instead of pretending login succeeded with an
 *   empty token and grants.
 */
export function refactorLoginResponse(data) {
  const isPlainObject = data !== null && typeof data === 'object'
  if (!isPlainObject) {
    throw new Error('Invalid login response')
  }

  const bearerToken = data.bearer_token ?? null

  const rawGrants = data.grants
  const sourceGrants =
    rawGrants && typeof rawGrants === 'object' && !Array.isArray(rawGrants) ? rawGrants : {}

  /** @type {Record<string, { codbId: unknown, companyName: unknown, shareable: unknown, website: unknown }>} */
  const grants = {}

  for (const grantId of Object.keys(sourceGrants)) {
    const entry = sourceGrants[grantId]
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      grants[grantId] = {
        codbId: null,
        companyName: null,
        shareable: null,
        website: null,
      }
      continue
    }

    grants[grantId] = {
      codbId: entry.codb_id ?? null,
      companyName: entry.company_name ?? null,
      shareable: entry.shareable ?? null,
      website: entry.website ?? null,
    }
  }

  return { bearerToken, grants }
}

/**
 * Read the `grant_ids` array out of a link-invite bearer JWT body.
 *
 * Analytics-only — we do NOT verify the signature. The server already
 * validates the token on every API call; this helper just reads the payload
 * so PostHog `Map View` events can be labeled with the correct grant.
 *
 * Returns `[]` for any malformed input rather than throwing — analytics
 * must never break the calling flow.
 *
 * @param {unknown} token Raw JWT string from sessionStorage `link_bearer_token`.
 * @returns {string[]}
 */
export function decodeBearerTokenGrantIds(token) {
  const payload = decodeJwtPayload(token)
  return Array.isArray(payload?.grant_ids)
    ? payload.grant_ids.filter((g) => typeof g === 'string')
    : []
}

/**
 * Maps share grant (`POST /share`) body fields to camelCase.
 *
 * @param {unknown} raw Raw API payload from link `POST /share`.
 * @returns {{ linkCode: unknown, linkGrantId: unknown }}
 */
export function refactorShareGrantResponse(raw) {
  const isPlainObject = raw !== null && typeof raw === 'object' && !Array.isArray(raw)
  if (!isPlainObject) {
    throw new Error('Invalid share grant response')
  }

  const linkCode = raw[SUBTRACE_GRANT_RESPONSE_KEYS.linkCode]
  const trimmedLinkCode = String(linkCode ?? '').trim()
  if (!trimmedLinkCode) {
    throw new Error('Missing link code in share grant response')
  }

  return {
    linkCode: trimmedLinkCode,
    linkGrantId: raw[SUBTRACE_GRANT_RESPONSE_KEYS.linkGrantId],
  }
}

/**
 * Full HTTPS URL for opening a grant via link-login (`/link/:linkId`; param value is API `link_code`).
 *
 * Accepts raw API (`link_code`) or refactored (`linkCode`) payloads.
 *
 * @param {unknown} response Share grant response body or refactored object.
 * @returns {string} Empty string when `response` is unusable or code is missing.
 */
export function getSubtraceGrantShareLink(response) {
  if (!response || typeof response !== 'object') {
    return ''
  }

  const k = SUBTRACE_GRANT_RESPONSE_KEYS
  const code = response[k.linkCode] ?? response.linkCode
  const trimmed = String(code ?? '').trim()
  if (!trimmed) {
    return ''
  }

  const base = resolveAppLinkOrigin().replace(/\/$/, '')
  return `${base}/link/${encodeURIComponent(trimmed)}`
}
